import { finite, canonicalCaseDimensions, TOPOLOGY_TRANSFORMS } from './geometry.js';

const BOARD_ORDER = ['ITX','DTX','MATX','ATX'];
const PSU_ORDER = ['FLEX','SFX','SFX-L','ATX'];
const FAMILIES = ['reference','sandwich','console'];

function normMobo(value) {
  const raw = String(value || '').toLowerCase().replace(/micro[-_ ]?atx|m[-_ ]?atx/g,'matx').replace(/mini[-_ ]?itx/g,'itx');
  if (raw.includes('matx')) return 'MATX';
  if (raw.includes('dtx')) return 'DTX';
  if (raw.includes('atx') && !raw.includes('itx')) return 'ATX';
  if (raw.includes('itx')) return 'ITX';
  return '';
}
function normPsu(value) {
  const raw = String(value || '').toLowerCase().replace(/\s+/g,'').replace(/-/g,'_');
  if (raw.includes('sfx_l') || raw.includes('sfxl')) return 'SFX-L';
  if (raw.includes('flex')) return 'FLEX';
  if (raw === 'atx' || (raw.includes('atx') && !raw.includes('sfx'))) return 'ATX';
  if (raw.includes('sfx')) return 'SFX';
  return '';
}

export function parseLayoutDescriptor(value) {
  const raw = String(value || '').trim().toLowerCase();
  const tokenText = raw.replace(/[_-]+/g,' ');
  const familyExplicit = /\b(sandwich|console|reference|classic)\b/.test(tokenText);
  const family = raw.includes('sandwich') ? 'sandwich' : raw.includes('console') ? 'console' : 'reference';
  return { raw, family, familyExplicit };
}

function activeModeDescriptor(effective) {
  const mode = effective?.activeMode;
  const text = `${mode?.spatial?.template || ''} ${mode?.layoutFamily || ''} ${mode?.layout || ''} ${mode?.style || ''} ${mode?.mode_name || ''} ${mode?.name || ''} ${mode?.mode_id || ''}`;
  return parseLayoutDescriptor(text);
}

function declaredFamilyCandidates(caseObj, overlay, effective, capabilityProfile) {
  const resolvedFamily = String(effective?.layoutFamily || '').toLowerCase();
  const resolvedSource = String(effective?.layoutFamilySource || '');

  // 1. The resolved ACTIVE MODE is authoritative over the base case declaration. This is
  // the critical distinction for flexible cases: a base REFERENCE case may expose a VGPU
  // slot/spine mode that resolves to SANDWICH without literally containing the word sandwich.
  if (effective?.activeMode && FAMILIES.includes(resolvedFamily)
      && (resolvedSource === 'active_mode_explicit' || resolvedSource === 'active_mode_inferred')) {
    return [resolvedFamily];
  }

  // 2. A real active mode that explicitly names its topology is authoritative.
  const active = activeModeDescriptor(effective);
  if (active.familyExplicit) return [active.family];

  // 3. Base structured spatial family is authoritative only when no mode-local family has
  // superseded it. A mode must never be pulled back to the case's base family here.
  const explicitSpatial = parseLayoutDescriptor(overlay?.spatial?.template || overlay?.layoutFamily || '');
  if (explicitSpatial.familyExplicit) return [explicitSpatial.family];

  const caseDeclared = parseLayoutDescriptor(caseObj?.style || caseObj?.layout || '');
  if (effective?.activeMode && caseDeclared.familyExplicit) return [caseDeclared.family];

  // 4. Explicit ML/structured family evidence is stronger than heuristics.
  const evidence = capabilityProfile?.deterministicLayoutEvidence || {};
  const evidenced = FAMILIES.filter(family => !!evidence[family]);
  if (evidenced.length === 1) return evidenced;

  // 5. A genuine spine/fixed-step relationship is strong evidence when no active mode has
  // already established a family. This deliberately rescues stale generic case labels.
  const hasModeLocalSteps = Array.isArray(effective?.activeMode?.fixedSteps) && effective.activeMode.fixedSteps.length > 0;
  const hasGlobalSteps = Array.isArray(overlay?.fixedSteps) && overlay.fixedSteps.length > 0;
  if (effective?.hasSpine || overlay?.hasSpine || hasModeLocalSteps || hasGlobalSteps) return ['sandwich'];

  // 6. Case/catalog label is useful, but weaker than structured relationships above.
  if (caseDeclared.familyExplicit) return [caseDeclared.family];

  const effectiveFamily = String(effective?.layoutFamily || '').toLowerCase();
  if (FAMILIES.includes(effectiveFamily)) return [effectiveFamily];
  return [...FAMILIES];
}

function topologyPrior(family, caseObj, overlay, effective, capabilityProfile = null) {
  const declaredFamily = String(effective?.layoutFamily || '').toLowerCase();
  const explicitSpatial = parseLayoutDescriptor(overlay?.spatial?.template || overlay?.layoutFamily || '');
  const active = activeModeDescriptor(effective);
  const resolvedModeFamily = effective?.activeMode
    && ['active_mode_explicit','active_mode_inferred'].includes(String(effective?.layoutFamilySource || ''))
    ? String(effective?.layoutFamily || '').toLowerCase()
    : '';
  const limits = effective?.limits || {};
  const reasons = [];
  let score = 0;

  if (declaredFamily === family) { score += 28; reasons.push(`family:${family}`); }
  else if (declaredFamily) score -= 14;

  if (resolvedModeFamily) {
    if (resolvedModeFamily === family) { score += 110; reasons.push('resolved_active_mode_family'); }
    else score -= 110;
  }

  if (explicitSpatial.familyExplicit) {
    if (explicitSpatial.family === family) { score += 90; reasons.push('structured_spatial_family'); }
    else score -= 90;
  }
  if (active.familyExplicit) {
    if (active.family === family) { score += 85; reasons.push('active_mode_family'); }
    else score -= 85;
  }

  const familyEvidence = capabilityProfile?.deterministicLayoutEvidence || {};
  if (familyEvidence[family]) { score += 62; reasons.push('structured_family_evidence'); }
  else if (Object.values(familyEvidence).some(Boolean)) score -= 20;

  const hasModeLocalSteps = Array.isArray(effective?.activeMode?.fixedSteps) && effective.activeMode.fixedSteps.length > 0;
  const fixedSteps = Array.isArray(overlay?.fixedSteps) ? overlay.fixedSteps.length : 0;
  // Do not let a global spine hint override a resolved active reference/console mode.
  const activeLocksNonSandwich = (resolvedModeFamily && resolvedModeFamily !== 'sandwich')
    || (active.familyExplicit && active.family !== 'sandwich');
  if (!activeLocksNonSandwich && (effective?.hasSpine || overlay?.hasSpine || hasModeLocalSteps || fixedSteps > 0)) {
    if (family === 'sandwich') { score += 72; reasons.push(effective?.hasSpine ? 'has_spine' : 'fixed_steps'); }
    else score -= 48;
  }

  const width = finite(caseObj?.w,0), length = finite(caseObj?.l,0), height = finite(caseObj?.h,0);
  const gpuL = finite(limits.maxGPU,0), gpuT = finite(limits.maxGPUT,0), cpuH = finite(limits.maxCPU,0);
  const widthPairs = capabilityProfile?.widthAllocationPairs?.length
    ? capabilityProfile.widthAllocationPairs
    : (gpuT > 0 && cpuH > 0 ? [{gpuT,cpuH,sum:gpuT+cpuH}] : []);
  if (width > 0 && widthPairs.length) {
    const ratios = widthPairs.map(pair => pair.sum / width);
    const close = ratios.filter(r => r >= 0.72 && r <= 1.14).length / ratios.length;
    const strong = ratios.filter(r => r >= 0.90 && r <= 1.05).length / ratios.length;
    if (family === 'sandwich' && strong >= 0.5) { score += 38; reasons.push('capability_pool_spine_signature'); }
    else if (family === 'sandwich' && close >= 0.5) { score += 14; reasons.push('capability_pool_width_signature'); }
  }

  // Console is a topology family, never a global "vertical" transform. Tall/thin dimensions
  // simply strengthen console evidence in the chassis' real world coordinate frame.
  if (width > 0 && width <= 125 && Math.max(length,height) >= 300 && gpuL >= 250) {
    if (family === 'console') { score += 36; reasons.push('thin_console_envelope'); }
    if (family === 'reference') score -= 10;
  }

  const supportMobo = capabilityProfile?.motherboardTypes?.length ? capabilityProfile.motherboardTypes : (Array.isArray(caseObj?.supportedMobo) ? caseObj.supportedMobo : []);
  if (supportMobo.some(v => ['MATX','ATX'].includes(normMobo(v))) && family === 'reference') {
    score += 10; reasons.push('large_board_support');
  }
  const supportPsu = capabilityProfile?.psuTypes || [];
  if (supportPsu.includes('ATX')) {
    if (family === 'reference') { score += 9; reasons.push('atx_psu_support'); }
    if (family === 'console' && width <= 125) score -= 7;
  }

  return { score, reasons };
}

export function enumerateTopologyHypotheses(caseObj, overlay, effective, capabilityProfile = null) {
  const families = declaredFamilyCandidates(caseObj, overlay, effective, capabilityProfile);
  const worldDims = [
    finite(caseObj?.w,150),
    finite(caseObj?.h,200),
    finite(caseObj?.l,300)
  ];
  return families.map(family => {
    const prior = topologyPrior(family, caseObj, overlay, effective, capabilityProfile);
    // Vertical-reference is a complete reference-frame transform. Sandwich/console retain
    // their V1.7 real-coordinate relationship logic. This keeps ordinary reference solving
    // as the single source of truth and projects every resulting component together.
    const verticalReference = family === 'reference'
      && effective?.layoutTransform === 'vertical'
      && effective?.modeTraits?.verticalReference === true;
    const transformClass = verticalReference ? TOPOLOGY_TRANSFORMS.vertical : TOPOLOGY_TRANSFORMS.standard;
    const canonicalDims = canonicalCaseDimensions(worldDims, transformClass);
    const canonical = { w:canonicalDims[0], h:canonicalDims[1], l:canonicalDims[2] };
    const variant = verticalReference ? 'vertical' : 'standard';
    return {
      id: `${family}__${variant}`,
      family,
      variant,
      transformClass,
      canonical,
      priorScore: prior.score,
      priorReasons: verticalReference ? [...prior.reasons,'vertical_reference_frame_transform'] : prior.reasons
    };
  }).sort((a,b) => b.priorScore-a.priorScore);
}

export function largestSupportedMotherboard(caseObj) {
  const values = Array.isArray(caseObj?.supportedMobo) ? caseObj.supportedMobo.map(normMobo).filter(Boolean) : [];
  if (!values.length) return 'ITX';
  return values.sort((a,b)=>BOARD_ORDER.indexOf(b)-BOARD_ORDER.indexOf(a))[0] || 'ITX';
}

export function largestSupportedPsu(caseObj) {
  const values = Array.isArray(caseObj?.supportedPSU) ? caseObj.supportedPSU.map(normPsu).filter(Boolean) : [];
  if (!values.length) return 'SFX';
  return values.sort((a,b)=>PSU_ORDER.indexOf(b)-PSU_ORDER.indexOf(a))[0] || 'SFX';
}
